import { type LineSymbol } from '@ikegps/core-api';
import { m, type MithrilVNode } from '../mithril';
import { rgba } from './theme';
import { WHITE } from './theme.palette';

export interface Pt {
	x: number;
	y: number;
}

export interface PathPlacement extends Pt {
	/** Direction of travel along the path at this point, in radians. */
	angle: number;
	/** Arc-distance of this point from the start of the path. */
	dist: number;
}

/** Flip an angle by π when it would render text upside-down (matches the map line label rule). */
export function uprightAngle(angle: number): number {
	return angle > Math.PI / 2 || angle < -Math.PI / 2 ? angle + Math.PI : angle;
}

interface Measured {
	/** Cumulative arc-distance at each vertex (`arcDistances[0] === 0`). */
	arcDistances: number[];
	total: number;
}

function measure(points: Pt[]): Measured {
	const arcDistances: number[] = [0];
	let total = 0;
	for (let i = 1; i < points.length; i++) {
		total += Math.hypot(points[i]!.x - points[i - 1]!.x, points[i]!.y - points[i - 1]!.y);
		arcDistances.push(total);
	}
	return { arcDistances, total };
}

function placementAt(points: Pt[], arcDistances: number[], dist: number): PathPlacement {
	let i = 0;
	while (i < points.length - 2 && arcDistances[i + 1]! <= dist) {
		i++;
	}
	const a = points[i]!;
	const b = points[i + 1]!;
	const segLen = arcDistances[i + 1]! - arcDistances[i]!;
	const t = segLen > 0 ? (dist - arcDistances[i]!) / segLen : 0;
	return {
		x: a.x + (b.x - a.x) * t,
		y: a.y + (b.y - a.y) * t,
		angle: Math.atan2(b.y - a.y, b.x - a.x),
		dist,
	};
}

/**
 * Place a marker every ~`spacing` px along the path, evenly distributed and inset by half a step so
 * the pattern is symmetric and never sits on an endpoint. Always yields at least one placement.
 */
export function walkPath(points: Pt[], spacing: number): PathPlacement[] {
	if (points.length < 2 || spacing <= 0) {
		return [];
	}
	const { arcDistances, total } = measure(points);
	if (total === 0) {
		return [];
	}
	const count = Math.max(1, Math.round(total / spacing));
	const step = total / count;
	const out: PathPlacement[] = [];
	for (let i = 0; i < count; i++) {
		out.push(placementAt(points, arcDistances, step * (i + 0.5)));
	}
	return out;
}

/** Split a polyline into sub-paths, removing the given arc-distance intervals (corners preserved). */
export function cutPolyline(points: Pt[], gaps: Array<[number, number]>): Pt[][] {
	if (points.length < 2) {
		return [];
	}
	if (gaps.length === 0) {
		return [points];
	}
	const { arcDistances, total } = measure(points);

	const merged: Array<[number, number]> = [];
	for (const [s, e] of [...gaps].sort((a, b) => a[0] - b[0])) {
		const start = Math.max(0, s);
		const end = Math.min(total, e);
		if (end <= start) {
			continue;
		}
		const last = merged[merged.length - 1];
		if (last && start <= last[1]) {
			last[1] = Math.max(last[1], end);
		} else {
			merged.push([start, end]);
		}
	}

	// Kept intervals = complement of the gaps within [0, total].
	const kept: Array<[number, number]> = [];
	let cursor = 0;
	for (const [s, e] of merged) {
		if (s > cursor) {
			kept.push([cursor, s]);
		}
		cursor = e;
	}
	if (cursor < total) {
		kept.push([cursor, total]);
	}

	return kept.map(([s, e]) => {
		const start = placementAt(points, arcDistances, s);
		const end = placementAt(points, arcDistances, e);
		const sub: Pt[] = [{ x: start.x, y: start.y }];
		for (let i = 0; i < points.length; i++) {
			if (arcDistances[i]! > s && arcDistances[i]! < e) {
				sub.push(points[i]!);
			}
		}
		sub.push({ x: end.x, y: end.y });
		return sub;
	});
}

const RAD_TO_DEG = 180 / Math.PI;

// Repeating-pattern tuning (px). Shared by the map polyline, the swatch preview, and the PDF legend.
const TIE_SPACING = 9;
const TIE_HALF_LENGTH = 5;
const CROSS_SPACING = 16;
const CROSS_HALF = 4;
const LABEL_SPACING = 90;
const LABEL_FONT_SIZE = 10;
const LABEL_CHAR_WIDTH = 0.62; // approx em width per char, for sizing the line break
const LABEL_GAP_PADDING = 4; // px of clearance each side of the text

// White hover-halo sizing: glyph strokes widen by HIGHLIGHT_PAD (matches the main stroke underlay),
// labels get a LABEL_HALO_STROKE outline drawn behind the fill.
const HIGHLIGHT_PAD = 2;
const LABEL_HALO_STROKE = 3;

export interface LineSymbolDecor {
	/** Arc-distance intervals to remove from the stroke so labels read clearly. */
	gaps: Array<[number, number]>;
	/** Overlay glyphs / labels drawn above the stroke. */
	nodes: MithrilVNode[];
	/** White halo copies of {@link nodes} for the hover highlight layer, drawn beneath them. */
	highlightNodes: MithrilVNode[];
}

const EMPTY: LineSymbolDecor = { gaps: [], nodes: [], highlightNodes: [] };

/** A straight glyph segment (railroad tie / fence cross arm) in pixel space. */
interface GlyphSeg {
	x1: number;
	y1: number;
	x2: number;
	y2: number;
}

/** A label placement: anchor point and the upright rotation (degrees) to lay the text along the line. */
interface LabelPlace {
	x: number;
	y: number;
	angleDeg: number;
}

interface LabelGeometry {
	gaps: Array<[number, number]>;
	places: LabelPlace[];
}

/** The two endpoints of a short horizontal swatch line, inset 2px from each end. */
export function swatchPath(width: number, height: number): Pt[] {
	const cy = height / 2;
	return [
		{ x: 2, y: cy },
		{ x: width - 2, y: cy },
	];
}

// Railroad: perpendicular ties repeated along the path.
function tieSegments(points: Pt[]): GlyphSeg[] {
	return walkPath(points, TIE_SPACING).map((p) => {
		const perpX = -Math.sin(p.angle) * TIE_HALF_LENGTH;
		const perpY = Math.cos(p.angle) * TIE_HALF_LENGTH;
		return { x1: p.x - perpX, y1: p.y - perpY, x2: p.x + perpX, y2: p.y + perpY };
	});
}

// Fence: drawn ✕ crosses (two diagonal arms) straddling the line, repeated along the path.
function crossSegments(points: Pt[]): GlyphSeg[] {
	const segs: GlyphSeg[] = [];
	walkPath(points, CROSS_SPACING).forEach((p) => {
		for (const arm of [p.angle + Math.PI / 4, p.angle - Math.PI / 4]) {
			const dx = Math.cos(arm) * CROSS_HALF;
			const dy = Math.sin(arm) * CROSS_HALF;
			segs.push({ x1: p.x - dx, y1: p.y - dy, x2: p.x + dx, y2: p.y + dy });
		}
	});
	return segs;
}

// Utility codes (GAS, FO, …): evenly spaced placements plus the stroke gaps knocked out around each.
function labelGeometry(points: Pt[], label: string): LabelGeometry {
	const places = walkPath(points, LABEL_SPACING);
	const gapHalf = (label.length * LABEL_FONT_SIZE * LABEL_CHAR_WIDTH) / 2 + LABEL_GAP_PADDING;
	const gaps = places.map((p): [number, number] => [p.dist - gapHalf, p.dist + gapHalf]);
	return {
		gaps,
		places: places.map((p) => ({ x: p.x, y: p.y, angleDeg: uprightAngle(p.angle) * RAD_TO_DEG })),
	};
}

/** SVG stroke-dasharray for a symbol, or undefined for a solid line. */
export function lineSwatchDashArray(symbol: LineSymbol): string | undefined {
	if (symbol.stroke === 'dash') {
		return (symbol.dash ?? [6, 4]).join(' ');
	}
	if (symbol.stroke === 'dotted') {
		return `0.1 ${Math.max(symbol.weight * 1.8, 3)}`;
	}
	return undefined;
}

/** The coloured stroke `<polyline>`s for a symbol, broken around any label `gaps`. */
export function lineSymbolStrokeNodes(
	points: Pt[],
	sym: LineSymbol,
	gaps: Array<[number, number]>,
	key: string
): MithrilVNode[] {
	const dash = lineSwatchDashArray(sym);
	const lineCap = sym.stroke === 'dotted' ? 'round' : 'butt';
	return cutPolyline(points, gaps).map((sub, i) =>
		m('polyline', {
			key: `${key}-stroke-${i}`,
			points: sub.map((p) => `${p.x},${p.y}`).join(' '),
			fill: 'none',
			stroke: sym.color,
			'stroke-width': `${sym.weight}px`,
			...(dash && { 'stroke-dasharray': dash }),
			'stroke-linecap': lineCap,
			'stroke-linejoin': 'round',
		})
	);
}

// Coloured glyph segments (ties / crosses) plus their white hover halos.
function glyphDecor(
	segs: GlyphSeg[],
	sym: LineSymbol,
	key: string,
	kind: string
): Pick<LineSymbolDecor, 'nodes' | 'highlightNodes'> {
	const nodes: MithrilVNode[] = [];
	const highlightNodes: MithrilVNode[] = [];
	segs.forEach((s, i) => {
		highlightNodes.push(
			m('line', {
				key: `${key}-${kind}-hl-${i}`,
				...s,
				stroke: rgba(WHITE),
				'stroke-width': `${sym.weight + HIGHLIGHT_PAD}px`,
				'stroke-linecap': 'round',
			})
		);
		nodes.push(m('line', { key: `${key}-${kind}-${i}`, ...s, stroke: sym.color, 'stroke-width': `${sym.weight}px` }));
	});
	return { nodes, highlightNodes };
}

// Rotated utility labels plus their white outline (stroke painted under the fill) for the hover halo.
function labelDecorNodes(
	places: LabelPlace[],
	sym: LineSymbol,
	key: string
): Pick<LineSymbolDecor, 'nodes' | 'highlightNodes'> {
	const label = sym.label!;
	const nodes: MithrilVNode[] = [];
	const highlightNodes: MithrilVNode[] = [];
	places.forEach((p, i) => {
		const common = {
			x: p.x,
			y: p.y,
			'text-anchor': 'middle',
			'dominant-baseline': 'central',
			'font-size': LABEL_FONT_SIZE,
			transform: `rotate(${p.angleDeg} ${p.x} ${p.y})`,
		};
		highlightNodes.push(
			m(
				'text',
				{
					key: `${key}-label-hl-${i}`,
					...common,
					fill: rgba(WHITE),
					stroke: rgba(WHITE),
					'stroke-width': `${LABEL_HALO_STROKE}px`,
					'stroke-linejoin': 'round',
					'paint-order': 'stroke',
				},
				label
			)
		);
		nodes.push(m('text', { key: `${key}-label-${i}`, ...common, fill: sym.color }, label));
	});
	return { nodes, highlightNodes };
}

/**
 * Repeating decorations for a line symbol along a pixel path: railroad ties, fence crosses, or
 * utility labels. Returns the overlay nodes plus any stroke gaps (label knockouts) the caller
 * should cut from the line with {@link cutPolyline}.
 */
export function lineSymbolDecor(points: Pt[], sym: LineSymbol, key = ''): LineSymbolDecor {
	if (points.length < 2) {
		return EMPTY;
	}
	if (sym.marker === 'tick') {
		return { gaps: [], ...glyphDecor(tieSegments(points), sym, key, 'tie') };
	}
	if (sym.marker === 'x') {
		return { gaps: [], ...glyphDecor(crossSegments(points), sym, key, 'x') };
	}
	if (sym.label) {
		const geometry = labelGeometry(points, sym.label);
		return { gaps: geometry.gaps, ...labelDecorNodes(geometry.places, sym, key) };
	}
	return EMPTY;
}

function svgAttrs(attrs: Record<string, string | number>): string {
	return Object.entries(attrs)
		.map(([k, v]) => `${k}="${escapeXml(String(v))}"`)
		.join(' ');
}

function escapeXml(value: string): string {
	return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * A self-contained `<svg>` markup string for a line symbol swatch — the same stroke / dash / glyph /
 * label rendering the on-screen {@link LineSwatch} produces, serialised so pdfMake's `svg` content can
 * draw it in the print legend (its `canvas` primitive can't render the utility-code text labels).
 * No hover halos: the legend swatch is static.
 */
export function lineSymbolSwatchSvg(symbol: LineSymbol, width: number, height: number): string {
	const path = swatchPath(width, height);
	const dash = lineSwatchDashArray(symbol);
	const lineCap = symbol.stroke === 'dotted' ? 'round' : 'butt';

	let gaps: Array<[number, number]> = [];
	let glyphs: GlyphSeg[] = [];
	let labels: LabelPlace[] = [];
	if (symbol.marker === 'tick') {
		glyphs = tieSegments(path);
	} else if (symbol.marker === 'x') {
		glyphs = crossSegments(path);
	} else if (symbol.label) {
		const geometry = labelGeometry(path, symbol.label);
		gaps = geometry.gaps;
		labels = geometry.places;
	}

	const parts: string[] = [];
	for (const sub of cutPolyline(path, gaps)) {
		parts.push(
			`<polyline ${svgAttrs({
				points: sub.map((p) => `${p.x},${p.y}`).join(' '),
				fill: 'none',
				stroke: symbol.color,
				'stroke-width': symbol.weight,
				...(dash && { 'stroke-dasharray': dash }),
				'stroke-linecap': lineCap,
				'stroke-linejoin': 'round',
			})}/>`
		);
	}
	for (const s of glyphs) {
		parts.push(`<line ${svgAttrs({ ...s, stroke: symbol.color, 'stroke-width': symbol.weight })}/>`);
	}
	for (const p of labels) {
		const attrs = svgAttrs({
			x: p.x,
			y: p.y,
			'text-anchor': 'middle',
			'dominant-baseline': 'central',
			'font-family': 'sans-serif',
			'font-size': LABEL_FONT_SIZE,
			transform: `rotate(${p.angleDeg} ${p.x} ${p.y})`,
			fill: symbol.color,
		});
		parts.push(`<text ${attrs}>${escapeXml(symbol.label ?? '')}</text>`);
	}

	return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${parts.join('')}</svg>`;
}
